import BaseMultipleAutocomplete, {
  TypeOption,
} from "components/app/BaseMultipleAutocomplete/BaseMultipleAutocomplete";
import { Controller, useForm } from "react-hook-form";
import React, { FC, useContext, useEffect, useState } from "react";
import { EditOutlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { ICellRendererParams } from "ag-grid-community";
import { useAxios } from "myAxios";
import { toast } from "react-toastify";
import { mutate } from "swr";
import AuthCheck from "components/app/AuthCheck";
import { FEATURE_KEY } from "constant";
import { AuthProvider } from "pages";
import { classHOC } from "components/app/ClassWrapper";

// interface AnchorEditorProps extends Partial<ICellRendererParams> {}
let cachedWidth = 0;
const AnchorEditor: FC<Partial<ICellRendererParams> & { minWidth?: number }> = ({
  node,
  data,
  value: valueFromProps,
  minWidth,
  columnApi,
  column,
}) => {
  const { control, getValues } = useForm();
  const [show, setShow] = useState(false);
  const onClick = () => setShow((prv) => !prv);
  const authDetail = useContext(AuthProvider);
  const onDoubleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    if (authDetail.features.includes(FEATURE_KEY.EDIT_ANCHOR_PRODUCTS) && e.detail === 2) {
      setShow(true);
    }
  };
  const value: string[] = data?.anchorProducts || [];
  const axios = useAxios();
  const closeCallback = async () => {
    setShow(false);
    const { anchorProducts: rawAnchorProducts } = getValues();
    const anchorProducts = rawAnchorProducts.map((x: TypeOption) => `${x.value}`);
    try {
      await axios.post(
        `/api/dataservice/v1/accounts/${data.accountKey}/anchor-products`,
        anchorProducts
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to update anchor products");
      return;
    }
    node?.updateData({
      ...data,
      anchorProducts,
    });
    mutate(`/api/dataservice/v1/accounts/${data.accountId}?includeSites=true&usingBundle=true`);
    mutate(`/api/dataservice/v1/accounts/${data.accountKey}/anchor-products/histories`);
  };

  useEffect(() => {
    if (columnApi && column && minWidth !== undefined) {
      if (show) {
        const currentWidth = column.getActualWidth();

        if (currentWidth < minWidth) {
          cachedWidth = currentWidth;
          columnApi.setColumnWidth(column, minWidth);
        }
      } else {
        if (cachedWidth) {
          columnApi.setColumnWidth(column, cachedWidth);
          cachedWidth = 0;
        }
      }
    }
  }, [show]);

  return (
    <>
      <AuthCheck id={FEATURE_KEY.EDIT_ANCHOR_PRODUCTS}>
        <IconButton
          size="small"
          className={"hover-show"}
          onClick={onClick}
        >
          <EditOutlined style={{ color: "#00a0d1", fontSize: "1rem" }} />
        </IconButton>
      </AuthCheck>
      {!show && <span onClick={onDoubleClick}>{valueFromProps}</span>}
      {show && (
        <Controller
          control={control}
          render={(props) => (
            <BaseMultipleAutocomplete
              {...props}
              category={"ANCHOR_PRODUCT"}
              showMarginLeft={false}
              fullWidth={true}
              closeCallback={closeCallback}
              minWidth={minWidth}
            />
          )}
          name={"anchorProducts"}
          rules={{
            required: { value: true, message: "This field is required" },
          }}
          defaultValue={
            value?.length
              ? value?.map((p) => {
                  const [type, value] = p.split("%-%");
                  return {
                    name: value ? `Other - ${value}` : type,
                    value: p,
                    type: value ? "Other" : "Standard",
                  };
                })
              : []
          }
        />
      )}
    </>
  );
};

export default classHOC(AnchorEditor);
